"use client";

import { Button } from "@/components/ui/button";
import { Link } from "@/i18n/navigation";
import { ChevronRight, Badge as BadgeIcon } from "lucide-react";

interface QuickStartCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  ctaText: string;
  ctaHref: string;
  badge?: string;
  variant?: "default" | "highlight" | "soon";
}

export function QuickStartCard({
  icon,
  title,
  description,
  ctaText,
  ctaHref,
  badge,
  variant = "default",
}: QuickStartCardProps) {
  const isComingSoon = variant === "soon";

  return (
    <div
      className={`
        relative rounded-lg border p-6 transition-all duration-200
        ${
          variant === "highlight"
            ? "bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200 hover:shadow-lg hover:scale-105"
            : "bg-white border-gray-200 hover:shadow-lg hover:scale-105"
        }
        ${isComingSoon ? "opacity-60" : ""}
      `}
    >
      {/* Badge */}
      {badge && (
        <div className="absolute top-3 right-3">
          <span
            className={`
              text-xs font-semibold px-2 py-1 rounded-full
              ${
                variant === "highlight"
                  ? "bg-purple-200 text-purple-700"
                  : "bg-blue-100 text-blue-700"
              }
            `}
          >
            {badge}
          </span>
        </div>
      )}

      {/* Coming Soon Overlay */}
      {isComingSoon && (
        <div className="absolute inset-0 rounded-lg flex items-center justify-center bg-white/50 backdrop-blur-sm">
          <span className="bg-amber-100 text-amber-700 px-3 py-1 rounded-full text-sm font-semibold">
            Coming Soon
          </span>
        </div>
      )}

      {/* Icon */}
      <div
        className={`
          w-12 h-12 rounded-lg flex items-center justify-center mb-4
          ${
            variant === "highlight"
              ? "bg-purple-100 text-purple-600"
              : "bg-gray-100 text-gray-600"
          }
        `}
      >
        <div className="text-2xl">{icon}</div>
      </div>

      {/* Content */}
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
      <p className="text-sm text-gray-600 mb-4 line-clamp-2">
        {description}
      </p>

      {/* CTA Button */}
      <Link href={ctaHref}>
        <Button
          variant={variant === "highlight" ? "default" : "outline"}
          size="sm"
          className={`w-full ${
            variant === "highlight" ? "bg-purple-600 hover:bg-purple-700" : ""
          } ${isComingSoon ? "pointer-events-none" : ""}`}
          disabled={isComingSoon}
        >
          {ctaText}
          {!isComingSoon && <ChevronRight className="w-4 h-4 ml-2" />}
        </Button>
      </Link>
    </div>
  );
}
